/**
 * ChartView — the natal wheel, the planets / houses / angles / aspects tables and the
 * glossary popup. Pure presentation: no data fetching. Shared by the own natal chart screen
 * and the saved-chart screen.
 *
 * Layout: [wheel + `header`] · `children` · tables. `children` lets a screen put its own
 * blocks (e.g. the paid reading) between the wheel and the tables.
 * Opt-in `details`: the tables go under a collapsed toggle, followed by `details.after`.
 */

import { useState, useCallback, useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { NatalChartSvg } from './NatalChartSvg';
import type { NatalSelectKind } from './NatalChartSvg';
import { InfoPopup } from './InfoPopup';
import { ChartDetailsToggle } from './ChartDetailsToggle';
import { ASPECT_GLOSSARY } from './natalGlossary';
import { PlanetsTable, HouseOccupancyTable, AnglesTable } from './chartViewTables';
import { AspectLegendTable, AspectGrid, ChartAspectsTable } from './chartViewAspects';
import { fmtDeg, localizeEntry, planetLabel, resolveSelection, type PopupState } from './chartViewHelpers';
import { normalizeChartViewData, type ChartViewData } from './chartViewAdapter';
import styles from '../../screens/me/MeSubpages.module.css';

export type { ChartViewData } from './chartViewAdapter';

export interface ChartViewProps {
  data: ChartViewData;
  /** Rendered inside the wheel block, under the wheel. */
  header?: ReactNode;
  /** Rendered between the wheel block and the tables. */
  children?: ReactNode;
  /** The tables sit under a toggle (collapsed at first) with this label; `after` is shown below it, always visible. */
  details?: { label: string; after?: ReactNode };
  'data-testid'?: string;
}

export function ChartView({ data: rawData, header, children, details, 'data-testid': testId }: ChartViewProps): JSX.Element {
  const { t } = useTranslation(['me', 'common']);
  // The server sends lowercase signs; labels and glossary keys are capitalised.
  const data = useMemo(() => normalizeChartViewData(rawData), [rawData]);
  const [popup, setPopup] = useState<PopupState | null>(null);

  const handleSelect = useCallback((kind: NatalSelectKind, id: string) => {
    const next = resolveSelection(kind, id, t);
    if (next) setPopup(next);
  }, [t]);

  const openAspect = useCallback((type: string, extra?: { from: string; to: string; orb: number }) => {
    const entry = ASPECT_GLOSSARY[type];
    if (!entry) return;
    const loc = localizeEntry('aspect', type, entry, t);
    if (extra) {
      setPopup({
        entry: {
          title: `${planetLabel(extra.from, t)} ${loc.title.toLowerCase()} ${planetLabel(extra.to, t)}`,
          subtitle: `${entry.angle}° · ${t('natalChart.chrome.colOrb')} ${fmtDeg(extra.orb)}`,
          body: loc.body,
        },
        glyph: entry.glyph,
      });
    } else {
      setPopup({ entry: loc, glyph: entry.glyph });
    }
  }, [t]);

  const tables = (
    <>
      {data.planets.length > 0 && (
        <PlanetsTable planets={data.planets} onPick={handleSelect} />
      )}
      {data.houses && data.houses.length > 0 && data.planets.length > 0 && (
        <HouseOccupancyTable houses={data.houses} planets={data.planets} onPick={handleSelect} />
      )}
      {data.angles && <AnglesTable angles={data.angles} onPick={handleSelect} />}
      <AspectLegendTable onPickType={openAspect} />
      {data.aspects && data.aspects.length > 0 && (
        <ChartAspectsTable aspects={data.aspects} onPick={handleSelect} onPickAspect={openAspect} />
      )}
      {data.aspects && data.aspects.length > 0 && data.planets.length > 1 && (
        <AspectGrid planets={data.planets} aspects={data.aspects} onPickAspect={openAspect} />
      )}
    </>
  );

  return (
    <div data-testid={testId}>
      <div className={styles.chartHead}>
        <NatalChartSvg
          size={300}
          planets={data.planets}
          houses={data.houses}
          angles={data.angles}
          aspects={data.aspects}
          onSelect={handleSelect}
        />
        {header}
      </div>

      {children}

      {details ? (
        <>
          <ChartDetailsToggle label={details.label}>{tables}</ChartDetailsToggle>
          {details.after}
        </>
      ) : tables}

      <InfoPopup
        open={popup !== null}
        title={popup?.entry.title ?? ''}
        subtitle={popup?.entry.subtitle}
        body={popup?.entry.body ?? ''}
        glyph={popup?.glyph}
        onClose={() => setPopup(null)}
      />
    </div>
  );
}
